import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { router } from './router'
import vuetify from './plugins/vuetify'
import axios from '@/utils/apiClient'
import 'element-plus/dist/index.css'
import './styles/index.scss'
import './assets/index.css'
import '@mdi/font/css/materialdesignicons.css'

// Third-party components
import VueDatePicker from '@vuepic/vue-datepicker'
import VueCal from 'vue-cal'
import VueDragscroll from 'vue-dragscroll'
import print from 'vue3-print-nb'
import JsonExcel from 'vue-json-excel3'
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar'
import VueScrollTo from 'vue-scrollto'
import VueApexCharts from 'vue3-apexcharts'
import VueTablerIcons from 'vue-tabler-icons'
import SmGlobalComponents from '@/plugins/sm-global-components'
import ElementPlus from 'element-plus'

import VueSweetalert2 from 'vue-sweetalert2'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'

// DateRangePicker
import DateRangePicker from 'vue3-daterange-picker'

// i18n
import { createI18n } from 'vue-i18n'
import messages from '@/utils/locales/messages'

const locale = localStorage.getItem('locale') || 'en'
const i18n = createI18n({
    locale,
    messages,
    legacy: false,
    fallbackLocale: 'en',
    silentTranslationWarn: true,
    silentFallbackWarn: true,
})

// Vue Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
            retry: 2,
        },
    },
})

// Create Vue app
const app = createApp(App)

// Register plugins
app.use(createPinia())
app.use(router)
app.use(i18n)
app.use(VueQueryPlugin, { queryClient })
app.use(VueDragscroll)
app.use(VueTablerIcons)
app.use(print)
app.use(VueApexCharts)
app.use(SmGlobalComponents)
app.use(ElementPlus)
app.use(PerfectScrollbarPlugin)
app.use(VueSweetalert2)
app.use(vuetify)

// Register global components
app.component('DateRangePicker', DateRangePicker)
app.component('EasyDataTable', Vue3EasyDataTable)
app.component('VueDatePicker', VueDatePicker)
app.component('vue-cal', VueCal)
app.component('download-excel', JsonExcel)

// Global properties
app.config.globalProperties.$axios = axios

// Scroll-to plugin
app.use(VueScrollTo, { duration: 1000, easing: 'ease' })


// Handle PWA install prompt
let deferredPrompt: BeforeInstallPromptEvent | null = null

window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault()
    deferredPrompt = e as BeforeInstallPromptEvent

    if (!document.getElementById('pwa-install-container')) {
        // Create styles
        const style = document.createElement('style')
        style.textContent = `
            #pwa-install-container {
                position: fixed;
                bottom: 24px;
                right: 24px;
                z-index: 9999;
                display: flex;
                align-items: center;
                background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                color: white;
                padding: 12px 20px;
                border-radius: 14px;
                box-shadow: 0 10px 25px -5px rgba(79, 70, 229, 0.4);
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                font-family: 'Inter', system-ui, -apple-system, sans-serif;
                font-weight: 600;
                font-size: 14px;
                border: 1px solid rgba(255, 255, 255, 0.1);
                letter-spacing: 0.01em;
            }
            #pwa-install-container:hover {
                transform: translateY(-4px) scale(1.02);
                box-shadow: 0 20px 30px -5px rgba(79, 70, 229, 0.5);
                filter: brightness(1.05);
            }
            #pwa-install-container:active {
                transform: translateY(-1px) scale(0.98);
            }
            #pwa-install-container svg.install-icon {
                margin-right: 10px;
                width: 20px;
                height: 20px;
                filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
            }
            #pwa-close-btn {
                background: rgba(255, 255, 255, 0.15);
                border: none;
                color: white;
                margin-left: 12px;
                cursor: pointer;
                padding: 4px;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
            }
            #pwa-close-btn:hover {
                background: rgba(255, 255, 255, 0.25);
                transform: rotate(90deg);
            }
            @media (max-width: 640px) {
                #pwa-install-container {
                    bottom: 100px; /* Lift above bottom nav */
                    left: 50%;
                    right: auto;
                    transform: translateX(-50%);
                    width: auto;
                    padding: 10px 16px;
                    border-radius: 50px; /* Pill shape on mobile */
                    box-shadow: 0 12px 30px -10px rgba(79, 70, 229, 0.6);
                }
                #pwa-install-container:hover {
                    transform: translateX(-50%) translateY(-2px);
                }
                #pwa-install-container:active {
                    transform: translateX(-50%) scale(0.95);
                }
            }
        `
        document.head.appendChild(style)

        const container = document.createElement('div')
        container.id = 'pwa-install-container'
        
        // Add Install Icon (SVG)
        container.innerHTML = `
            <svg class="install-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
            </svg>
            <span>Install App</span>
            <button id="pwa-close-btn" title="Dismiss">
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
            </button>
        `
        
        document.body.appendChild(container)

        // Install logic
        container.addEventListener('click', async (e) => {
            // Don't trigger install if close button was clicked
            if ((e.target as HTMLElement).closest('#pwa-close-btn')) return

            if (!deferredPrompt) return
            deferredPrompt.prompt()
            const choice = await deferredPrompt.userChoice
            if (choice.outcome === 'accepted') {
                console.log('User accepted the install prompt')
                container.remove()
            }
            deferredPrompt = null
        })

        // Close logic
        const closeBtn = container.querySelector('#pwa-close-btn')
        closeBtn?.addEventListener('click', (e) => {
            e.stopPropagation()
            container.style.opacity = '0'
            container.style.pointerEvents = 'none'
            // Keep the horizontal position centered if on mobile
            const isMobile = window.innerWidth <= 640
            container.style.transform = isMobile 
                ? 'translateX(-50%) translateY(20px)' 
                : 'translateY(20px)'
            setTimeout(() => container.remove(), 300)
        })
    }
})

// Mount app after router is ready
router.isReady().then(() => app.mount('#app'))
